import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { Connection } from '@domain/connection/models/Connection';
import { APP_CONFIG } from '../../../../../config/app.config';
import { Icon } from '@shared/icons';

const colors = APP_CONFIG.ui.theme.colors;
const SHOPIFY_LOGO = '/assets/images/shopify-logo.png';

function normalizeShopDomain(shop: string): string {
  const trimmed = shop.trim().toLowerCase();
  return trimmed.includes('.myshopify.com') ? trimmed : `${trimmed}.myshopify.com`;
}

export function shopMatchesDomain(connection: Connection, shopDomain?: string): boolean {
  if (!shopDomain || !connection.name) return false;
  return normalizeShopDomain(connection.name) === normalizeShopDomain(shopDomain);
}

interface SelectShopifyShopStepProps {
  shops: Connection[];
  selectedConnectionId?: string;
  previouslyBilledShopDomain?: string;
  onSelect: (connectionId: string) => void;
  onContinue: () => void;
  onAddConnection: () => void;
  onReconnect: (connectionId: string) => void;
}

export const SelectShopifyShopStep: React.FC<SelectShopifyShopStepProps> = ({
  shops,
  selectedConnectionId,
  previouslyBilledShopDomain,
  onSelect,
  onContinue,
  onAddConnection,
  onReconnect,
}) => {
  const selected = shops.find(shop => shop.id === selectedConnectionId);
  const canContinue = Boolean(selected?.authorized);

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Choose which Shopify store should manage this subscription.
      </Typography>

      <Stack spacing={1.5}>
        {shops.map(shop => {
          const isDisconnected = !shop.authorized;
          const isSelected = !isDisconnected && shop.id === selectedConnectionId;
          const isPrevious = shopMatchesDomain(shop, previouslyBilledShopDomain);
          const logo = shop.connector.image || SHOPIFY_LOGO;

          return (
            <Box
              key={shop.id}
              onClick={isDisconnected ? undefined : () => onSelect(shop.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                textAlign: 'left',
                p: 2,
                borderRadius: '8px',
                border: `1px solid ${isSelected ? colors.primary : colors.border}`,
                bgcolor: isSelected ? colors.primaryTint : colors.background,
                cursor: isDisconnected ? 'default' : 'pointer',
                '&:hover': isDisconnected ? undefined : {
                  borderColor: colors.borderHover,
                },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  borderRadius: '50%',
                  border: `2px solid ${isDisconnected ? colors.border : isSelected ? colors.primary : colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: colors.primary,
                    }}
                  />
                )}
              </Box>
              <Avatar
                src={logo}
                alt="Shopify"
                variant="rounded"
                sx={{
                  width: 48,
                  height: 32,
                  bgcolor: colors.background,
                  border: `1px solid ${colors.border}`,
                  '& img': { objectFit: 'contain', p: 0.5 },
                }}
              />
              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: colors.primaryText,
                    wordBreak: 'break-word',
                  }}
                >
                  {shop.name}
                </Typography>

                {isPrevious && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.secondaryText, flexShrink: 0 }}>
                    Previously billed
                  </Typography>
                )}

              </Stack>

              {isDisconnected && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<Icon icon="ArrowRefresh03" />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReconnect(shop.id);
                  }}
                >
                  Reconnect
                </Button>
              )}
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<Icon icon="Plus01" />}
          onClick={onAddConnection}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            py: 1.5,
            px: 2,
            borderRadius: '8px',
            borderStyle: 'dashed',
            borderColor: colors.border,
            color: colors.primaryText,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 600,
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: colors.borderHover,
              bgcolor: colors.primaryTint,
            },
          }}
        >
          Add connection
        </Button>
      </Stack>

      <Button
        variant="contained"
        onClick={onContinue}
        disabled={!canContinue}
      >
        Continue
      </Button>
    </Stack>
  );
};
